<template>
  <div
    v-if="item?.salesPackageSize"
    :class="{
      'product-package-size-fi': true,
      'product-package-size-fi--align-right': alignRight,
    }"
  >
    {{ noBrackets ? '' : '(' }}{{ label }}{{ noBrackets ? '' : ')' }}
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { useSellingUnits } from '@/composables/sellingUnit';
import type { HybrisFischerBestPrice } from '@/generated/graphql';

defineOptions({
  name: 'ProductPackageSizeFi',
});

const props = defineProps<{
  item?: HybrisFischerBestPrice | null;
  alignRight?: boolean;
  noBrackets?: boolean;
  /**
   * The quoting cart's wording: the size is stated from the selected unit's ratio, and the
   * "/ package" half is dropped when it is a single selected unit, since the cart's unit
   * select already shows it. Every other caller keeps the original wording.
   */
  hideSelectedPackage?: boolean;
}>();

const { getSellingUnit, getSellingPackage } = useSellingUnits();

// The original wording, used by every caller except the quoting cart.
const legacyDefaultPackage = computed(() =>
  props.item?.alternativeUnits?.find((el) => el?.denominator === el?.numerator),
);

const legacyAlternativeUnit = computed(() =>
  props.item?.alternativeUnits?.find((el) => el?.unit === props.item?.salesPackageUnit),
);

const legacyPackageSize = computed(() => {
  if (props.item?.alternativeUnits?.length && props.item?.salesPackageUnit) {
    if (legacyAlternativeUnit.value) {
      return legacyAlternativeUnit.value.denominator >= legacyAlternativeUnit.value.numerator
        ? legacyAlternativeUnit.value.denominator
        : legacyAlternativeUnit.value.numerator;
    }
  }

  return props.item?.salesPackageSize;
});

const legacySellingUnit = computed(() => {
  if (
    props.item?.salesPackageUnit &&
    (!legacyAlternativeUnit.value ||
      legacyAlternativeUnit.value.denominator > legacyAlternativeUnit.value.numerator)
  ) {
    return getSellingUnit(props.item?.salesPackageUnit);
  }

  return getSellingUnit(legacyDefaultPackage.value?.unit || '');
});

const legacySellingPackage = computed(() => {
  if (
    props.item?.salesPackageUnit &&
    legacyAlternativeUnit.value &&
    legacyAlternativeUnit.value.denominator >= legacyAlternativeUnit.value.numerator
  ) {
    return getSellingPackage(legacyDefaultPackage.value?.unit || '');
  }

  return getSellingPackage(props.item?.salesPackageUnit);
});

// The quoting cart's wording from here on.

// Several units can be 1:1, e.g. ST and PAK when a package holds one piece; the last wins.
const defaultPackage = computed(() => {
  const baseUnits = props.item?.alternativeUnits?.filter((el) => el?.denominator === el?.numerator);
  return baseUnits?.[baseUnits.length - 1];
});

// A package is stated in pieces, as "1 Pkg. / Pkg." says nothing.
const alternativeUnit = computed(() => {
  const units = props.item?.alternativeUnits;
  const selected = props.item?.salesPackageUnit;
  return (
    (selected === 'PAK' ? units?.find((el) => el?.unit === 'ST') : undefined) ??
    units?.find((el) => el?.unit === selected)
  );
});

// numerator base units make denominator selected units, the larger count leading:
// "40 Pkg. / 2 Karton", but "100 Stk / Pkg." for pieces. A count of 1 after the slash is
// dropped.
const swapped = computed(
  () =>
    !!alternativeUnit.value && alternativeUnit.value.denominator > alternativeUnit.value.numerator,
);

const baseUnit = computed(() => getSellingUnit(defaultPackage.value?.unit || ''));
const selectedUnit = computed(() =>
  getSellingUnit(alternativeUnit.value?.unit || props.item?.salesPackageUnit || ''),
);

const packageSize = computed(() => {
  if (!alternativeUnit.value) {
    return props.item?.salesPackageSize;
  }
  return swapped.value ? alternativeUnit.value.denominator : alternativeUnit.value.numerator;
});

const sellingUnit = computed(() =>
  alternativeUnit.value && !swapped.value ? baseUnit.value : selectedUnit.value,
);

const packageCount = computed(() =>
  swapped.value ? alternativeUnit.value?.numerator : alternativeUnit.value?.denominator,
);

const sellingPackage = computed(() => {
  if (!alternativeUnit.value) {
    return getSellingPackage(props.item?.salesPackageUnit);
  }
  const unit = swapped.value ? baseUnit.value : selectedUnit.value;
  return packageCount.value === 1 ? unit : `${packageCount.value} ${unit}`;
});

const packageUnitCode = computed(() => {
  if (!alternativeUnit.value) {
    return props.item?.salesPackageUnit;
  }
  return swapped.value ? defaultPackage.value?.unit : alternativeUnit.value.unit;
});

const showPackage = computed(
  () => (packageCount.value ?? 1) !== 1 || packageUnitCode.value !== props.item?.salesPackageUnit,
);

const label = computed(() => {
  if (!props.hideSelectedPackage) {
    return `${legacyPackageSize.value} ${legacySellingUnit.value} / ${legacySellingPackage.value}`;
  }
  return `${packageSize.value} ${sellingUnit.value}${showPackage.value ? ` / ${sellingPackage.value}` : ''}`;
});
</script>

<style scoped src="./product-package-size-fi" />
